/**
 * Copyright 2018 Phenix Inc. All Rights Reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
define([
    'phenix-web-lodash-light',
    'phenix-web-assert',
    'phenix-web-disposable'
], function(_, assert, disposable) {
    'use strict';

    var requestMethods = {
        'get': 'GET',
        'post': 'POST',
        'put': 'PUT',
        'delete': 'DELETE' // Delete is reserved
    };
    var defaultTimeout = 5000;
    var defaultMaxAttempts = 1;
    var defaultBackoff = 2;
    var defaultRetryDelay = 1500;
    var errorCodes = {
        clientAborted: {
            id: 4000,
            name: 'Aborted by Client'
        }
    };

    function Http() {

    }

    Http.prototype.get = function get(url, settings, callback) {
        settings = settings || {};

        validateSettings(settings);

        var requestUrl = appendQueryParameters(settings.queryParameters || {}, url);
        var xhr = getAndOpenVendorSpecificXmlHttpMethod(requestMethods.get, requestUrl, callback);
        var handleResponse = _.bind(appendResponseTimeAndContinue, this, _.now(), callback);

        if (!xhr) {
            return callback(getUnsupportedError());
        }

        if (settings.mimeType) {
            xhr.overrideMimeType(settings.mimeType);
        }

        if (settings.responseType) {
            xhr.responseType = settings.responseType;
        }

        appendDataTypeHeaders(xhr, settings);

        xhr.addEventListener('readystatechange', _.bind(handleReadyStateChange, this, xhr, settings, handleResponse));

        xhr.timeout = settings.timeout || defaultTimeout;

        xhr.send();
    };

    Http.prototype.post = function postWithRetry(url, data, settings, callback) {
        return methodWithData.call(this, requestMethods.post, url, data, settings, callback);
    };

    Http.prototype.put = function postWithRetry(url, data, settings, callback) {
        return methodWithData.call(this, requestMethods.put, url, data, settings, callback);
    };

    Http.prototype.delete = function postWithRetry(url, data, settings, callback) {
        return methodWithData.call(this, requestMethods.delete, url, data, settings, callback);
    };

    Http.prototype.getWithRetry = function getWithRetry(url, settings, callback) {
        var methodWithoutCallback = _.bind(this.get, this, url, settings);
        var requestState = {
            startTime: _.now(),
            isDisposed: false
        };

        return handleMethodWithRetry.call(this, url, settings, methodWithoutCallback, requestState, callback);
    };

    Http.prototype.postWithRetry = function postWithRetry(url, data, settings, callback) {
        return methodRetryWithData.call(this, this.post, url, data, settings, callback);
    };

    Http.prototype.putWithRetry = function postWithRetry(url, data, settings, callback) {
        return methodRetryWithData.call(this, this.put, url, data, settings, callback);
    };

    Http.prototype.deleteWithRetry = function postWithRetry(url, data, settings, callback) {
        return methodRetryWithData.call(this, this.delete, url, data, settings, callback);
    };

    function methodWithData(method, url, data, settings, callback) {
        settings = settings || {};

        validateSettings(settings);

        var requestUrl = appendQueryParameters(settings.queryParameters || {}, url);
        var xhr = getAndOpenVendorSpecificXmlHttpMethod(method, requestUrl, callback);
        var handleResponse = _.bind(appendResponseTimeAndContinue, this, _.now(), callback);

        if (!xhr) {
            return callback(getUnsupportedError());
        }

        if (settings.responseType) {
            xhr.responseType = settings.responseType;
        }

        appendDataTypeHeaders(xhr, settings);

        xhr.addEventListener('readystatechange', _.bind(handleReadyStateChange, this, xhr, settings, handleResponse));

        xhr.timeout = settings.timeout || 15000;

        xhr.send(data);
    }

    function methodRetryWithData(method, url, data, settings, callback) {
        var methodWithoutCallback = _.bind(method, this, url, data, settings);
        var requestState = {
            startTime: _.now(),
            isDisposed: false
        };

        return handleMethodWithRetry.call(this, url, settings, methodWithoutCallback, requestState, callback);
    }

    function validateSettings(settings) {
        assert.isObject(settings, 'settings');

        if (settings.queryParameters) {
            assert.isObject(settings.queryParameters, 'settings.queryParameters');
        }

        if (settings.mimeType) {
            assert.stringNotEmpty(settings.mimeType, 'settings.mimeType');
        }

        if (settings.accept) {
            assert.stringNotEmpty(settings.accept, 'settings.accept');
        }

        if (settings.contentType) {
            assert.stringNotEmpty(settings.contentType, 'settings.contentType');
        }

        if (settings.responseType) {
            assert.stringNotEmpty(settings.responseType, 'settings.responseType');
        }

        if (!_.isNullOrUndefined(settings.timeout)) {
            assert.isNumber(settings.timeout, 'settings.timeout');
        }
    }

    function appendQueryParameters(queryParameters, url) {
        var indexOfHash = url.lastIndexOf('#');
        var indexOfQuerySeparator = url.indexOf('?');
        var serializedJsonAsQueryString = _.reduce(queryParameters, function(paramString, value, key) {
            paramString += !paramString ? '' : '&';

            return paramString + key.toString() + '=' + value.toString();
        }, '');
        var hashString = indexOfHash === -1 ? '' : url.substring(indexOfHash + 1);
        var queryString = indexOfQuerySeparator === -1 ? '' : url.substring(indexOfQuerySeparator + 1, hashString ? indexOfHash : url.length);
        var concatenatedQueryString = serializedJsonAsQueryString + (queryString ? ('&' + queryString) : '');
        var urlBeforeQueryAndHash = url.replace('?' + queryString, '').replace('#' + hashString, '');

        url = urlBeforeQueryAndHash;

        if (concatenatedQueryString) {
            url += '?' + concatenatedQueryString;
        }

        if (hashString) {
            url += '#' + hashString;
        }

        return url;
    }

    function getAndOpenVendorSpecificXmlHttpMethod(requestMethod, requestUrl) {
        var xhr = new XMLHttpRequest();

        if ('withCredentials' in xhr) {
            // Most browsers.
            xhr.open(requestMethod, requestUrl, true);
        } else if (typeof XDomainRequest !== 'undefined') {
            // IE8 & IE9
            xhr = new XDomainRequest();
            xhr.open(requestMethod, requestUrl);
        } else {
            return;
        }

        return xhr;
    }

    function getUnsupportedError() {
        // CORS not supported.
        var err = new Error('unsupported');

        err.code = 'unsupported';

        return err;
    }

    function appendDataTypeHeaders(xhr, settings) {
        var contentType = settings.contentType || 'application/json';
        var accept = settings.accept || contentType;

        var headers = _.assign({}, {
            'Content-Type': contentType,
            'Accept': accept
        }, _.get(settings, ['headers'], {}));

        _.forOwn(headers, function(headerValue, headerName) {
            xhr.setRequestHeader(headerName, headerValue);
        });
    }

    function handleReadyStateChange(xhr, options, callback) {
        if (xhr.readyState === 4 /* DONE */) {
            if (xhr.status === 200) {
                var responseHeaders = getXhrResponseHeaders(xhr);
                var response = {
                    data: xhr.response || xhr.responseText,
                    headers: responseHeaders,
                    rawXhr: xhr
                };

                callback(null, response);
            } else {
                var err = new Error(xhr.status === 0 ? 'timeout' : 'Status=[' + xhr.status + ']');
                var additionalRetryErrorCodes = _.get(options, ['retryOptions', 'additionalRetryErrorCodes'], []);

                _.forEach(additionalRetryErrorCodes, function(code) {
                    assert.isNumber(code, 'additionalRetryErrorCode');
                });

                err.code = xhr.status;

                if ((xhr.status >= 500 && xhr.status < 600) || _.includes(additionalRetryErrorCodes, xhr.status)) {
                    err.retryable = true;
                }

                callback(err);
            }
        }
    }

    function getXhrResponseHeaders(xhr) {
        var responseHeadersString = xhr.getAllResponseHeaders();

        return _.reduce(responseHeadersString.trim().split(/[\r\n]+/), function(headers, header) {
            var parts = header.split(': ');
            var headerName = parts.shift();
            var headerValue = parts.join(': ');

            if (headerName) {
                headers[headerName] = headerValue;
            }

            return headers;
        }, {});
    }

    function appendResponseTimeAndContinue(startTime, callback, error, response) {
        if (response) {
            _.set(response, ['stats', 'successResponseTime'], _.now() - startTime);
        }

        callback(error, response);
    }

    function appendTotalResponseTimeAndContinue(startTime, attempts, callback, error, response) {
        if (response) {
            _.set(response, ['stats', 'totalResponseTimeForAllAttempts'], _.now() - startTime);
            _.set(response, ['stats', 'attempts'], attempts);
        }

        callback(error, response);
    }

    function handleMethodWithRetry(url, options, methodWithoutCallback, requestState, callback, attempt) {
        attempt = attempt || 1;

        var that = this;
        var initalRequestDelay = _.get(options, ['delay'], null);
        var retryDelay = _.get(options, ['retryOptions', 'delay'], defaultRetryDelay);
        var backoff = _.get(options, ['retryOptions', 'backoff'], defaultBackoff);
        var maxAttempts = _.get(options, ['retryOptions', 'maxAttempts'], defaultMaxAttempts);
        var requestDelay = attempt > 1 ? retryDelay * Math.pow(attempt, backoff) : initalRequestDelay;

        var retryIfFailed = function(error, response) {
            if (requestState.isDisposed) {
                return callback(getErrorByEnum(errorCodes.clientAborted));
            }

            if (error && error.retryable && attempt < maxAttempts) {
                return handleMethodWithRetry.call(that, url, options, methodWithoutCallback, requestState, callback, attempt + 1);
            }

            return appendTotalResponseTimeAndContinue.call(that, requestState.startTime, attempt, callback, error, response);
        };

        var disposableRequest = new disposable.Disposable(function() {
            if (_.isNumber(requestState.mostRecentTimeout)) {
                clearTimeout(requestState.mostRecentTimeout);
            }

            requestState.isDisposed = true;
        });

        if (!_.isNumber(initalRequestDelay) && attempt === 1 || _.get(options, ['retryOptions', 'sync'], false)) {
            methodWithoutCallback.call(that, retryIfFailed);

            return disposableRequest;
        }

        delayedRequest.call(that, url, options, methodWithoutCallback, requestState, requestDelay, retryIfFailed);

        return disposableRequest;
    }

    function delayedRequest(url, options, methodWithoutCallback, requestState, delay, callback) {
        var that = this;

        requestState.mostRecentTimeout = setTimeout(function() {
            if (requestState.isDisposed) {
                return callback(getErrorByEnum(errorCodes.clientAborted));
            }

            methodWithoutCallback.call(that, callback);
        }, delay);
    }

    function getErrorByEnum(codeEnum) {
        var errorWithEnum = new Error(codeEnum.name);

        errorWithEnum.code = codeEnum.id;

        return errorWithEnum;
    }

    return new Http();
});